package er.prateek.crossly.content

import er.prateek.crossly.data.model.DifficultyTier
import er.prateek.crossly.data.model.Puzzle
import er.prateek.crossly.persistence.ContentCacheStore

private val packPathByTier = mapOf(
    DifficultyTier.EASY to "files/packs/core-crossword-easy-v1.json",
    DifficultyTier.MEDIUM to "files/packs/core-crossword-medium-v1.json",
    DifficultyTier.HARD to "files/packs/core-crossword-hard-v1.json",
    DifficultyTier.EXPERT to "files/packs/core-crossword-expert-v1.json",
)

private val packIdByTier = mapOf(
    DifficultyTier.EASY to "core-crossword-easy-v1",
    DifficultyTier.MEDIUM to "core-crossword-medium-v1",
    DifficultyTier.HARD to "core-crossword-hard-v1",
    DifficultyTier.EXPERT to "core-crossword-expert-v1",
)

/**
 * Levels built from the word bank by the `generatePuzzlePacks` Gradle task.
 * These are read exactly like the authored packs -- a few milliseconds of JSON
 * parsing -- because generating them on demand costs seconds per tier and the
 * home screen totals every tier, which put the whole search on the startup path.
 */
private val generatedPackPathByTier = mapOf(
    DifficultyTier.EASY to "files/packs/generated/generated-crossword-easy-v1.json",
    DifficultyTier.MEDIUM to "files/packs/generated/generated-crossword-medium-v1.json",
    DifficultyTier.HARD to "files/packs/generated/generated-crossword-hard-v1.json",
    DifficultyTier.EXPERT to "files/packs/generated/generated-crossword-expert-v1.json",
)

/**
 * Bundled content is the permanent offline floor. [refreshFromRemote] overlays
 * any pack whose remote manifest version is strictly newer than what's
 * bundled or already cached; the cache then supersedes bundled on every
 * subsequent read until the next successful, newer refresh -- Phase 5's
 * merge-by-version rule. [remote] is nullable: with no remote source
 * configured, this behaves exactly like the bundled-only Phase 0-4 repository.
 */
class ContentRepositoryImpl(
    private val bundled: BundledContentSource = BundledContentSource(),
    private val cache: ContentCacheStore? = null,
    private val remote: RemoteContentSource? = null,
    /** Set only to bypass the bundled generated packs; null in production. */
    private val generated: GeneratedPuzzleSource? = null,
    private val includeGeneratedPacks: Boolean = true,
) : ContentRepository {
    private val puzzlesCacheByTier = mutableMapOf<DifficultyTier, List<Puzzle>>()

    /**
     * Authored puzzles first -- their clues are hand-written and they act as the
     * tier's introduction -- then generated ones to give the tier real depth.
     *
     * Authored puzzles are filtered through [isWellFormed] on the way through.
     * Three of the bundled EASY puzzles are symmetric word squares whose across
     * and down answers are the same words (SUN/USE/NET both ways), which reads
     * as a bug to players. Dropping them here fixes the symptom at the single
     * point every caller goes through, and means a bad authored puzzle can
     * never reach the board again.
     */
    override suspend fun puzzlesByTier(tier: DifficultyTier): List<Puzzle> {
        puzzlesCacheByTier[tier]?.let { return it }
        val packId = packIdByTier.getValue(tier)
        val bundledPack = bundled.loadCrosswordPack(packPathByTier.getValue(tier))
        val cachedPack = cache?.loadPack(packId)
        val resolved = if (cachedPack != null && cachedPack.packVersion > bundledPack.packVersion) {
            cachedPack
        } else {
            bundledPack
        }

        val authored = resolved.puzzles.filter { it.isWellFormed() }
        val fromPacks = if (includeGeneratedPacks) {
            bundled.loadCrosswordPack(generatedPackPathByTier.getValue(tier))
                .puzzles
                .filter { it.isWellFormed() }
        } else {
            emptyList()
        }
        val combined = authored + fromPacks + generated?.puzzlesFor(tier).orEmpty()
        return combined.also { puzzlesCacheByTier[tier] = it }
    }

    override suspend fun puzzleById(id: String): Puzzle? {
        for (tier in DifficultyTier.entries) {
            puzzlesByTier(tier).firstOrNull { it.id == id }?.let { return it }
        }
        return null
    }

    override suspend fun refreshFromRemote() {
        val remoteSource = remote ?: return
        val cacheStore = cache ?: return
        val manifest = remoteSource.fetchManifest() ?: return
        cacheStore.saveManifest(manifest)

        for (entry in manifest.packs) {
            val tier = packIdByTier.entries.firstOrNull { it.value == entry.packId }?.key ?: continue
            val bundledPack = bundled.loadCrosswordPack(packPathByTier.getValue(tier))
            val currentVersion = maxOf(bundledPack.packVersion, cacheStore.loadPack(entry.packId)?.packVersion ?: 0)
            if (entry.version <= currentVersion) continue

            val fetched = remoteSource.fetchPack(entry) ?: continue
            cacheStore.savePack(fetched)
            // Invalidate the in-memory read cache so the newly fetched pack takes effect immediately.
            puzzlesCacheByTier.remove(tier)
        }
    }
}
